/* iter55p13 — Author fan-mail inbox panel.
 *
 * Lightweight sheet-style modal listing every unread + read message
 * the author has received via /api/authors/<slug>/contact. Mark-read
 * happens on click (POST /api/profile/me/contact-inbox/<id>/read).
 *
 * Opens from the User Profile Panel's header (unread bell) so the
 * author never has to leave the profile surface to check mail.
 *
 * Feb-2026 iter — Archive + Delete controls per message plus a
 * "Show archived" toggle. Same soft-archive / hard-delete semantics
 * as the admin inbox so the UX is consistent across the two mailboxes.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Loader2, Mail, User, Archive, ArchiveRestore, Trash2,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function fmt(iso) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.round(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export default function AuthorInboxPanel({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // { id, action }

  const load = (opts = {}) => {
    const includeArchived = opts.includeArchived ?? showArchived;
    setBusy(true);
    axios.get(`${API}/profile/me/contact-inbox`, {
      params: { limit: 100, include_archived: includeArchived },
      withCredentials: true,
    })
      .then((r) => {
        setRows(r.data?.messages || []);
        setUnread(r.data?.unread || 0);
        setArchivedCount(r.data?.archived || 0);
      })
      .catch(() => toast.error('Could not load inbox.'))
      .finally(() => setBusy(false));
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [open, showArchived]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/profile/me/contact-inbox/${id}/read`, {}, { withCredentials: true });
      setRows((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* silent */ }
  };

  const archiveMsg = async (id) => {
    setRowBusy({ id, action: 'archive' });
    try {
      await axios.post(`${API}/profile/me/contact-inbox/${id}/archive`, {}, { withCredentials: true });
      toast.success('Message archived');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not archive.'); }
    finally { setRowBusy(null); }
  };
  const unarchiveMsg = async (id) => {
    setRowBusy({ id, action: 'unarchive' });
    try {
      await axios.post(`${API}/profile/me/contact-inbox/${id}/unarchive`, {}, { withCredentials: true });
      toast.success('Message restored');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore.'); }
    finally { setRowBusy(null); }
  };
  const deleteMsg = async (id) => {
    if (!window.confirm('Permanently delete this fan message? This cannot be undone.')) return;
    setRowBusy({ id, action: 'delete' });
    try {
      await axios.delete(`${API}/profile/me/contact-inbox/${id}`, { withCredentials: true });
      toast.success('Message deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete.'); }
    finally { setRowBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="author-inbox-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Fan mail
            {unread > 0 && (
              <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground"
                    data-testid="inbox-unread-badge">
                {unread} unread
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowArchived((s) => !s)}
              data-testid="inbox-archived-toggle"
              className={`ml-auto rounded-sm border px-2 py-0.5 text-[10.5px] transition-colors ${showArchived ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border bg-background hover:bg-panel-header/60'}`}
            >
              {showArchived ? 'Hide archived' : `Show archived${archivedCount ? ` (${archivedCount})` : ''}`}
            </button>
          </DialogTitle>
          <DialogDescription>
            Messages sent to you via the public author page. Reply directly from
            your email client — the sender&apos;s address is right there.
          </DialogDescription>
        </DialogHeader>

        {busy && rows.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            {showArchived
              ? 'No archived messages yet.'
              : <>No messages yet. Turn on <em className="mx-1">Fan mail</em> in your profile to accept them.</>}
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1" data-testid="inbox-message-list">
            {rows.map((m) => {
              const rowBusyHere = rowBusy?.id === m.id;
              return (
              <div
                key={m.id}
                data-testid={`inbox-msg-${m.id}`}
                className={
                  'group flex gap-2 rounded-md border p-3 text-left transition ' +
                  (m.archived
                    ? 'border-muted-foreground/20 bg-muted/40 opacity-80'
                    : m.read ? 'bg-card' : 'bg-primary/5 border-primary/40')
                }
              >
                <button
                  type="button"
                  onClick={() => !m.read && markRead(m.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-7 w-7 shrink-0 rounded-full bg-muted p-1.5">
                        <User className="h-full w-full text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-semibold">{m.from_name}</div>
                        <a
                          href={`mailto:${m.from_email}`}
                          className="text-[11px] text-muted-foreground hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {m.from_email}
                        </a>
                      </div>
                    </div>
                    <div className="text-[10.5px] text-muted-foreground shrink-0 ml-2">
                      {fmt(m.at)}
                      {!m.read && !m.archived && (
                        <span className="ml-2 inline-block h-2 w-2 rounded-full bg-primary" aria-label="unread" />
                      )}
                      {m.archived && (
                        <span className="ml-2 rounded bg-muted px-1 py-px text-[9.5px] uppercase tracking-wider">
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed">
                    {m.body}
                  </p>
                </button>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  {m.archived ? (
                    <button
                      type="button"
                      onClick={() => unarchiveMsg(m.id)}
                      disabled={rowBusyHere}
                      title="Restore to inbox"
                      className="rounded p-1 text-muted-foreground hover:bg-panel-header/60 hover:text-foreground disabled:opacity-50"
                      data-testid={`inbox-msg-unarchive-${m.id}`}
                    >
                      {rowBusyHere && rowBusy?.action === 'unarchive'
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <ArchiveRestore className="h-3.5 w-3.5" />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => archiveMsg(m.id)}
                      disabled={rowBusyHere}
                      title="Archive message"
                      className="rounded p-1 text-muted-foreground hover:bg-panel-header/60 hover:text-foreground disabled:opacity-50"
                      data-testid={`inbox-msg-archive-${m.id}`}
                    >
                      {rowBusyHere && rowBusy?.action === 'archive'
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Archive className="h-3.5 w-3.5" />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteMsg(m.id)}
                    disabled={rowBusyHere}
                    title="Delete message"
                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    data-testid={`inbox-msg-delete-${m.id}`}
                  >
                    {rowBusyHere && rowBusy?.action === 'delete'
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
